/**
 * Authenticated gameplay session context — NOT global.
 *
 * Mounted inside AuthLayout only (after login + profile load). Public pages do not have this context.
 *
 * Subscribes to the user's leagues, league member docs, gameUpdates sentinels/results, and fixtures
 * (profile uses a filtered fixture window). Exposes sessionPhase, tournamentStarted, allowTeamSwap,
 * and useGameSession() to profile, league, and related authed routes.
 */
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from 'react';
import { useLocation } from 'react-router-dom';
import {
  collection,
  collectionGroup,
  documentId,
  onSnapshot,
  orderBy,
  query,
  where,
} from 'firebase/firestore';
import type { Fixture, League } from '@/models';
import type { leagueMember } from '@/models/user';
import { type gameUpdate, queryGameUpdates, sentinelType } from '@/models/gameUpdate';
import { FirebaseContext } from '@/session/FirebaseContext';
import { type Dict, toDict } from '@/helpers';
import { getFixturesFromJson } from '@/models/fixture';
import { ACTIVE_TOURNAMENT } from '@/constants';
import type { SessionPhase } from './types';

/** Profile match feed lookback (~110 minutes): recent + upcoming fixtures without loading the full schedule. */
const PROFILE_FIXTURES_LOOKBACK_MS = 60 * 110 * 1000;
const LEAGUE_ID_BATCH_SIZE = 30;

function chunkIds(ids: string[], size: number): string[][] {
  const batches: string[][] = [];
  for (let i = 0; i < ids.length; i += size) {
    batches.push(ids.slice(i, i + size));
  }
  return batches;
}

export type GameSessionContextValue = {
  leagues: Dict<League>;
  fixtures: Fixture[];
  allFixtures: Fixture[];
  memberData: Dict<leagueMember>;
  gameUpdates: Dict<gameUpdate>;
  tournamentStarted: boolean;
  allowTeamSwap: boolean;
  leaguesHydrated: boolean;
  membersHydrated: boolean;
  sessionPhase: SessionPhase;
  sessionError: Error | null;
};

const defaultValue: GameSessionContextValue = {
  leagues: {},
  fixtures: [],
  allFixtures: getFixturesFromJson(),
  memberData: {},
  gameUpdates: {},
  tournamentStarted: false,
  allowTeamSwap: false,
  leaguesHydrated: false,
  membersHydrated: false,
  sessionPhase: 'idle',
  sessionError: null,
};

const GameSessionContext = createContext<GameSessionContextValue>(defaultValue);

export function GameSessionProvider({ children }: PropsWithChildren) {
  const ctx = useContext(FirebaseContext);
  const loc = useLocation();
  const needsLiveFixtures = loc.pathname === '/profile';
  const uid = ctx.user?.uid;

  const [allFixtures, setAllFixtures] = useState<Fixture[]>(() => getFixturesFromJson());
  const [leagues, setLeagues] = useState<Dict<League>>({});
  const [memberData, setMemberData] = useState<Dict<leagueMember>>({});
  const [leaguesHydrated, setLeaguesHydrated] = useState(false);
  const [membersHydrated, setMembersHydrated] = useState(false);
  const [gameUpdates, setGameUpdates] = useState<Dict<gameUpdate>>({});
  const [gameUpdatesReceived, setGameUpdatesReceived] = useState(false);
  const [tournamentStarted, setTournamentStarted] = useState(false);
  const [allowTeamSwap, setAllowTeamSwap] = useState(false);
  const [sessionError, setSessionError] = useState<Error | null>(null);

  const memberDataKey = useMemo(() => Object.keys(memberData).sort().join(','), [memberData]);

  useEffect(() => {
    setLeagues({});
    setMemberData({});
    setLeaguesHydrated(false);
    setMembersHydrated(false);
    setGameUpdates({});
    setGameUpdatesReceived(false);
    setTournamentStarted(false);
    setAllowTeamSwap(false);
    setSessionError(null);
    setAllFixtures(getFixturesFromJson());
  }, [uid]);

  useEffect(() => {
    return onSnapshot(
      queryGameUpdates(ctx.db, orderBy('timestamp', 'desc')),
      (snapshot) => {
        setSessionError(null);
        const _gameUpdates: Dict<gameUpdate> = toDict(snapshot);
        setGameUpdates(_gameUpdates);
        setGameUpdatesReceived(true);
        setTournamentStarted(
          _gameUpdates ? Object.keys(_gameUpdates).includes(sentinelType.tournamentStarted) : false,
        );
        setAllowTeamSwap(
          _gameUpdates ? Object.keys(_gameUpdates).includes(sentinelType.allowTeamSwap) : false,
        );
      },
      (error) => {
        console.error('[gameUpdates snapshot]', error);
        setSessionError(error);
      },
    );
  }, [ctx.db]);

  useEffect(() => {
    if (uid && needsLiveFixtures) {
      return onSnapshot(
        query(
          collection(ctx.db, 'competitions', ACTIVE_TOURNAMENT, 'fixtures'),
          orderBy('date', 'asc'),
          orderBy('sortOrder', 'asc'),
        ),
        (snapshot) => {
          const loaded = snapshot.docs.map((x) => x.data() as Fixture);
          if (loaded.length > 0) {
            setAllFixtures(loaded);
          }
        },
        (error) => {
          console.error('[fixtures snapshot]', error);
          setSessionError(error);
        },
      );
    } else if (!uid) {
      setAllFixtures(getFixturesFromJson());
    }
  }, [ctx.db, uid, needsLiveFixtures]);

  useEffect(() => {
    if (!uid) {
      return;
    }

    const leagueIds = memberDataKey ? memberDataKey.split(',') : [];
    if (leagueIds.length === 0) {
      setLeagues({});
      setLeaguesHydrated(true);
      return;
    }

    // Refresh league docs without flipping leaguesHydrated to false. Clearing hydration
    // would push sessionPhase back to loading and unmount AuthLayout's Outlet, wiping
    // in-page UI state (e.g. Support CTA) while module-scoped gates survive.
    setLeagues({});
    const batches = chunkIds(leagueIds, LEAGUE_ID_BATCH_SIZE);
    const batchesReady = new Set<number>();
    const mergedLeagues: Dict<League> = {};

    const unsubs = batches.map((batch, batchIndex) =>
      onSnapshot(
        query(collection(ctx.db, 'leagues'), where(documentId(), 'in', batch)),
        (snapshot) => {
          Object.assign(mergedLeagues, toDict(snapshot));
          setLeagues({ ...mergedLeagues });
          batchesReady.add(batchIndex);
          if (batchesReady.size === batches.length) {
            setLeaguesHydrated(true);
          }
        },
        (error) => {
          console.error('[leagues snapshot]', error);
          setSessionError(error);
        },
      ),
    );

    return () => {
      unsubs.forEach((unsub) => unsub());
    };
  }, [ctx.db, uid, memberDataKey]);

  useEffect(() => {
    if (!uid) {
      return;
    }

    return onSnapshot(
      query(collectionGroup(ctx.db, 'members'), where('userID', '==', uid)),
      (snapshot) => {
        setSessionError(null);
        setMemberData(toDict(snapshot, (x) => x.ref.parent.parent!.id));
        setMembersHydrated(true);
      },
      (error) => {
        console.error('[members snapshot]', error);
        setSessionError(error);
      },
    );
  }, [ctx.db, uid]);

  const fixtureCutoff = Date.now() - PROFILE_FIXTURES_LOOKBACK_MS;
  const fixtures = allFixtures.filter((f) => f.date.toMillis() > fixtureCutoff);

  const sessionPhase: SessionPhase = useMemo(() => {
    if (!uid) {
      return 'idle';
    }
    if (sessionError) {
      return 'error';
    }
    if (gameUpdatesReceived && membersHydrated && leaguesHydrated) {
      return 'ready';
    }
    return 'loading';
  }, [uid, sessionError, gameUpdatesReceived, membersHydrated, leaguesHydrated]);

  const value = useMemo<GameSessionContextValue>(
    () => ({
      leagues,
      fixtures,
      allFixtures,
      memberData,
      gameUpdates,
      tournamentStarted,
      allowTeamSwap,
      leaguesHydrated,
      membersHydrated,
      sessionPhase,
      sessionError,
    }),
    [
      leagues,
      fixtures,
      allFixtures,
      memberData,
      gameUpdates,
      tournamentStarted,
      allowTeamSwap,
      leaguesHydrated,
      membersHydrated,
      sessionPhase,
      sessionError,
    ],
  );

  return <GameSessionContext.Provider value={value}>{children}</GameSessionContext.Provider>;
}

export function useGameSession(): GameSessionContextValue {
  return useContext(GameSessionContext);
}
